import React, { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

/**
 * The MQ view for staff. Summaries only — profile name, cluster, top barriers —
 * and framed as a support instrument. The rule of the page: this data prompts a
 * conversation, never a consequence.
 */
export default function Wellbeing() {
  const s = useSession();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    const q = query(collection(db, "mq"), where("cohortId", "==", s.profile.cohortId), orderBy("assessedAt", "desc"));
    return onSnapshot(q, (snap) => setRows(snap.docs.map((d) => d.data())));
  }, [s.profile?.cohortId]);

  // Latest per resident, scoped to a master's cluster.
  const mine = s.role === "master" && s.profile?.clusterId
    ? rows.filter((r) => r.clusterId === s.profile.clusterId)
    : rows;
  const latest = {};
  for (const r of mine) if (!latest[r.residentUid]) latest[r.residentUid] = r;
  const list = Object.values(latest);

  const isolation = list.filter((r) => r.topBarriers?.some((b) => /isolation/i.test(b.name))).length;

  return (
    <div>
      <Eyebrow>Pod wellbeing · Ready Mindset</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Who needs a human this week</h1>
      <p style={{ color: "var(--graphite)", maxWidth: "66ch" }}>
        These are checkpoint summaries, not surveillance: profile and top barriers only, never raw answers,
        never scores-by-question. An Isolation Trap barrier is a pod-health signal; a Capacity Gap barrier is a
        load signal. The response to either is a conversation. This page has no connection to marks and never will.
      </p>

      {isolation > 0 && (
        <div className="card" style={{ margin: "14px 0", borderColor: "var(--bronze)" }}>
          <strong>{isolation}</strong> resident{isolation > 1 ? "s" : ""} in your view carry Isolation Trap in their
          top three. Check their pod's crit attendance before their journal goes quiet, not after.
        </div>
      )}

      <table style={{ marginTop: 10 }}>
        <thead><tr><th>Resident</th><th>Pod</th><th>Checkpoint</th><th>Level</th><th>Cluster</th><th>Top barriers</th></tr></thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.residentUid}>
              <td className="mono" style={{ fontSize: 12 }}>{r.residentUid.slice(0, 8)}</td>
              <td className="mono" style={{ fontSize: 12 }}>{r.podId ?? "—"}</td>
              <td>{r.checkpoint}</td>
              <td>{r.mqLevel}</td>
              <td>{r.profileCluster ?? "—"}</td>
              <td style={{ fontSize: 13 }}>{(r.topBarriers ?? []).map((b) => b.name).join(" · ")}</td>
            </tr>
          ))}
          {!list.length && (
            <tr><td colSpan={6} style={{ color: "var(--graphite)", padding: 18 }}>
              No checkpoints taken yet in your view.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
